import { useState, type FormEvent } from 'react';

import {
    Badge,
    Button,
    Card,
    Field,
    Input,
    Skeleton,
    StatusPill,
    type Tone,
} from '@/design-system';
import type { Device } from '@/entities/user';
import { errorMessage, isApiError } from '@/shared/api';
import { formatDayTimeShort } from '@/shared/lib';
import { ErrorState } from '@/shared/ui';

import { accountCopy } from '../copy';
import { useDevices, useRenameDevice, useRestoreDevice, useRevokeDevice } from '../hooks';

const SMALL = 'text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]';

function deviceStatus(device: Device): { tone: Tone; label: string } {
    if (device.revoked) {
        return { tone: 'neutral', label: accountCopy.statusRevoked };
    }

    return device.trusted
        ? { tone: 'accent', label: accountCopy.statusActive }
        : { tone: 'warn', label: accountCopy.statusExpired };
}

/**
 * Apparaten met toegang: alleen die van jezelf.
 *
 * Het apparaat waarop je dit leest staat bovenaan en is gemarkeerd. Dat trek je
 * hier niet in - dan zou je jezelf op dit apparaat buitensluiten; wie wil stoppen
 * logt uit. Een ander apparaat intrekken gaat via een bevestiging die zegt wat er
 * gebeurt. Een ingetrokken apparaat laat je hier weer toe - ook als een beheerder
 * het introk; daarna log je op dat apparaat opnieuw in met je wachtwoord.
 */
export function DevicesCard() {
    const devices = useDevices();

    return (
        <Card padding="lg" as="section" aria-labelledby="devices-title">
            <h2
                id="devices-title"
                className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {accountCopy.devicesTitle}
            </h2>
            <p className={`m-0 mt-1 mb-3 ${SMALL}`}>{accountCopy.devicesIntro}</p>

            {devices.isPending ? (
                <div aria-busy="true">
                    <Skeleton height={14} label={accountCopy.devicesLoading} />
                    <Skeleton width="70%" height={14} className="mt-2" />
                </div>
            ) : devices.isError ? (
                <ErrorState
                    error={devices.error}
                    title={accountCopy.devicesError}
                    onRetry={() => void devices.refetch()}
                />
            ) : devices.data.length === 0 ? (
                <p className="m-0 py-[9px] text-[13px] text-[var(--text-tertiary-safe)]">
                    {accountCopy.devicesEmpty}
                </p>
            ) : (
                <ul className="m-0 list-none p-0">
                    {devices.data.map((device) => (
                        <DeviceRow key={device.uuid} device={device} />
                    ))}
                </ul>
            )}
        </Card>
    );
}

function DeviceRow({ device }: { device: Device }) {
    const [mode, setMode] = useState<'idle' | 'rename' | 'revoke'>('idle');
    const [name, setName] = useState(device.name);
    const [nameError, setNameError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const rename = useRenameDevice();
    const revoke = useRevokeDevice();
    const restore = useRestoreDevice();
    const status = deviceStatus(device);
    const error =
        mode === 'rename' ? rename.error : mode === 'revoke' ? revoke.error : restore.error;

    function submitRename(event: FormEvent) {
        event.preventDefault();

        const next = name.trim();

        if (next === '') {
            setNameError(accountCopy.renameRequired);

            return;
        }

        setNameError(null);
        rename.mutate(
            { uuid: device.uuid, name: next },
            {
                onSuccess: (renamed) => {
                    setMode('idle');
                    setNotice(accountCopy.renamed(renamed.name));
                },
            },
        );
    }

    const facts = [
        device.revoked && device.revokedAt
            ? accountCopy.revokedOn(formatDayTimeShort(device.revokedAt))
            : device.trusted && device.trustedUntil
              ? accountCopy.trustedUntil(formatDayTimeShort(device.trustedUntil))
              : null,
        device.lastSeenAt
            ? accountCopy.lastSeen(formatDayTimeShort(device.lastSeenAt))
            : accountCopy.neverSeen,
        device.lastSyncAt ? accountCopy.lastSync(formatDayTimeShort(device.lastSyncAt)) : null,
    ].filter((fact): fact is string => fact !== null);

    return (
        <li className="border-b border-[var(--border-subtle-2)] py-[11px]">
            <div className="flex flex-wrap items-center gap-x-[10px] gap-y-1">
                <span className="min-w-0 flex-1 truncate text-[14px] leading-[1.35] font-medium text-[var(--text-primary)]">
                    {device.name}
                </span>
                {device.current ? (
                    <Badge variant="text" tone="accent">
                        {accountCopy.thisDevice}
                    </Badge>
                ) : null}
                <StatusPill tone={status.tone} size="sm">
                    {status.label}
                </StatusPill>
            </div>
            <p className={`m-0 mt-[2px] ${SMALL}`}>
                {`${device.platformLabel} · ${device.fieldDevice ? accountCopy.fieldDevice : accountCopy.officeDevice}`}
            </p>
            <p className={`m-0 ${SMALL}`}>{facts.join(' · ')}</p>

            {mode === 'idle' && !device.revoked ? (
                <div className="mt-[8px] flex flex-wrap items-center gap-2">
                    <Button
                        variant="outline"
                        size="xs"
                        onClick={() => {
                            setName(device.name);
                            setNotice(null);
                            rename.reset();
                            setMode('rename');
                        }}
                    >
                        {accountCopy.rename}
                    </Button>
                    {device.current ? null : (
                        <Button
                            variant="outline"
                            size="xs"
                            onClick={() => {
                                setNotice(null);
                                revoke.reset();
                                setMode('revoke');
                            }}
                        >
                            {accountCopy.revoke}
                        </Button>
                    )}
                </div>
            ) : null}

            {mode === 'idle' && device.revoked && !device.current ? (
                <div className="mt-[8px] flex flex-wrap items-center gap-2">
                    <Button
                        variant="outline"
                        size="xs"
                        disabled={restore.isPending}
                        onClick={() => {
                            setNotice(null);
                            restore.mutate(device.uuid, {
                                onSuccess: (restored) =>
                                    setNotice(accountCopy.restored(restored.name)),
                            });
                        }}
                    >
                        {accountCopy.restore}
                    </Button>
                </div>
            ) : null}

            {mode === 'idle' && device.current ? (
                <p className={`m-0 mt-2 ${SMALL}`}>{accountCopy.currentNote}</p>
            ) : null}

            {mode === 'rename' ? (
                <form
                    onSubmit={submitRename}
                    noValidate
                    className="mt-[10px] flex flex-wrap items-start gap-2"
                >
                    <Field
                        label={accountCopy.renameLabel(device.name)}
                        size="sm"
                        className="min-w-[200px] flex-1"
                        error={
                            nameError ??
                            (isApiError(rename.error)
                                ? rename.error.firstValidationError('name')
                                : undefined)
                        }
                    >
                        <Input
                            value={name}
                            maxLength={120}
                            autoComplete="off"
                            onChange={(event) => setName(event.target.value)}
                        />
                    </Field>
                    <div className="flex min-h-[64px] items-end gap-2">
                        <Button
                            type="submit"
                            variant="primary"
                            size="sm"
                            disabled={rename.isPending}
                        >
                            {accountCopy.renameSave}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setMode('idle')}>
                            {accountCopy.cancel}
                        </Button>
                    </div>
                </form>
            ) : null}

            {mode === 'revoke' ? (
                <Card
                    variant="warn"
                    padding="md"
                    radius={9}
                    className="mt-[10px]"
                    role="group"
                    aria-label={accountCopy.revokeLabel(device.name)}
                >
                    <p className="m-0 text-[13px] leading-[1.5] text-[var(--warn-ink)]">
                        {accountCopy.revokeBody(device.name)}
                    </p>
                    <div className="mt-[10px] flex flex-wrap gap-2">
                        <Button
                            variant="warn"
                            size="sm"
                            disabled={revoke.isPending}
                            onClick={() =>
                                revoke.mutate(device.uuid, {
                                    onSuccess: () => {
                                        setMode('idle');
                                        setNotice(accountCopy.revoked(device.name));
                                    },
                                })
                            }
                        >
                            {accountCopy.revokeYes}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setMode('idle')}>
                            {accountCopy.cancel}
                        </Button>
                    </div>
                </Card>
            ) : null}

            {notice ? (
                <p
                    role="status"
                    className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--accent-dark)]"
                >
                    {notice}
                </p>
            ) : null}

            {error && !(isApiError(error) && error.firstValidationError('name')) ? (
                <p role="alert" className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {errorMessage(error)}
                </p>
            ) : null}
        </li>
    );
}
